import { Builder, Input } from '@builder.io/sdk';
import dynamic from 'next/dynamic';
import { landingPageFormInputs } from '../LandingPageForm/LandingPageForm.config';

const PartnershipFormPanel = dynamic(() =>
  import('./PartnershipFormPanel').then((m) => ({ default: m.PartnershipFormPanel }))
);

const getFormInputs = (): Input[] => {
  const excludedFields = ['heading', 'subHeading'];

  const defaultOverrides: Record<string, string | boolean> = {
    regLabel: 'Enter Registration Number',
    formIntroText: 'To get started simply enter your reg...',
    submitButtonText: 'Get a quote',
    submitButtonColor: 'purple',
    useCardStyle: true,
    regPlateOnly: true,
    showDurationPicker: false,
  };

  const overrideDefaults = (input: Input): Input => {
    if (input.name in defaultOverrides) {
      return { ...input, defaultValue: defaultOverrides[input.name] };
    }
    return input;
  };

  return landingPageFormInputs.filter((input) => !excludedFields.includes(input.name)).map(overrideDefaults);
};

const PartnershipFormPanelConfig = {
  name: 'PartnershipFormPanel',
  friendlyName: 'Partnership Form Panel',
  inputs: [
    {
      name: 'partnershipLabel',
      type: 'string',
      friendlyName: 'Partnership Label',
      helperText: 'Text shown before the partner logo, e.g. "In partnership with:"',
      defaultValue: 'In partnership with:',
      required: false,
    },
    {
      name: 'partnershipLogo',
      type: 'file',
      friendlyName: 'Partnership Logo',
      helperText: 'Logo image for the partner brand',
      allowedFileTypes: ['png', 'jpg', 'jpeg', 'webp', 'svg'],
      required: false,
    },
    {
      name: 'partnershipLogoAlt',
      type: 'string',
      friendlyName: 'Partnership Logo Alt Text',
      helperText: 'Alt text for the partner logo',
      defaultValue: 'Partner logo',
      required: false,
      advanced: true,
    },
    {
      name: 'heading',
      type: 'string',
      friendlyName: 'Heading',
      helperText: 'Main heading displayed in the right column (desktop) or above the form (mobile)',
      defaultValue: 'Get cashback on your policy with TopCashback',
      required: false,
    },
    {
      name: 'subtitle',
      type: 'string',
      friendlyName: 'Subtitle',
      helperText: 'Subtitle displayed above the list items',
      defaultValue: 'How does cashback work?',
      required: false,
    },
    {
      name: 'listItems',
      type: 'list',
      friendlyName: 'List Items',
      helperText: 'Steps or benefits shown below the subtitle (max 6 items)',
      subFields: [
        {
          name: 'text',
          type: 'string',
          friendlyName: 'Item Text',
          helperText: 'Text content for this list item',
          required: true,
        },
      ],
      defaultValue: [
        { text: 'Get a quote from Tempcover to be eligible for a TopCashback payment.' },
        { text: 'Buy your policy once you have received this quote.' },
        { text: 'You can get up to £35 cashback depending on vehicle type and length of policy.' },
        { text: 'Your cashback will show as pending in your TopCashback account after 1 day.' },
      ],
    },
    {
      name: 'markerStyle',
      type: 'enum',
      friendlyName: 'List Marker Style',
      helperText: 'Choose numbered badges or checkmark icons for list items',
      defaultValue: 'numbers',
      enum: [
        { label: 'Numbers', value: 'numbers' },
        { label: 'Checkmarks', value: 'checkmarks' },
      ],
    },
    {
      name: 'backgroundImage',
      type: 'file',
      friendlyName: 'Background Image',
      helperText: 'Background image shown on desktop only (hidden on mobile for performance)',
      allowedFileTypes: ['png', 'jpg', 'jpeg', 'webp'],
      required: false,
    },
    {
      name: 'backgroundAlt',
      type: 'string',
      friendlyName: 'Background Image Alt Text',
      helperText: 'Alt text for the background image',
      defaultValue: 'Background image showing a curved road with a car driving on it',
      required: false,
      advanced: true,
    },
    {
      name: 'showRegLabel',
      friendlyName: 'Show Registration Label',
      type: 'boolean',
      helperText: 'Show or hide the label above the registration input',
      defaultValue: false,
    },
    {
      name: 'showRegToggle',
      friendlyName: 'Show "I don\'t know my reg" Toggle',
      type: 'boolean',
      helperText: 'Show or hide the "I don\'t know my reg yet" toggle link',
      defaultValue: false,
    },
    ...getFormInputs(),
  ] as Input[],
};

Builder.registerComponent(PartnershipFormPanel, PartnershipFormPanelConfig);
